/**
 * The one intermediate shape every Bruno front-end (.bru folder, .yml folder,
 * JSON export, bundled OpenCollection YAML) — and the Hoppscotch parser —
 * produces, so the mapping onto Apiboo's collection exists ONCE
 * (`toNormalized.js`) and the four Bruno formats cannot drift apart.
 *
 *   Collection = { name, docs, defaults: Defaults, children: Node[], environments: ImportedEnv[] }
 *   Node       = Folder | Request
 *   Folder     = { kind:'folder', name, seq, docs, defaults: Defaults, children: Node[] }
 *   Request    = { kind:'request', name, seq, reqType:'http'|'graphql'|'unsupported', typeName?,
 *                  method, url, headers: Row[], query: Row[], pathParams: Row[], body: Body,
 *                  auth: Auth, scripts: {pre, post, tests}, docs,
 *                  counts: { vars, assertions, examples } }
 *   Defaults   = { headers: Row[], auth: Auth|null (null = not set), vars: Row[], scripts: {pre, post, tests} }
 *   Row        = { key, value, enabled }
 *   Body       = { mode:'none'|'json'|'text'|'xml'|'sparql'|'urlencoded'|'multipart'|'graphql'|'file',
 *                  text?, rows?, graphql?: {query, variables} }
 *                multipart rows add { type:'text'|'file', src? }
 *   Auth       = { mode:'inherit'|'none'|'bearer'|'basic'|'apikey'|'awsv4'|'oauth2'|'unsupported',
 *                  token? | username?,password? | key?,value?,in? | accessKeyId?,… | oauth2? ,
 *                  unsupportedName? }
 *   ImportedEnv= { name, variables: [{ key, value, enabled, secret }] }
 */

export const emptyScripts = () => ({ pre: '', post: '', tests: '' });

export const emptyDefaults = () => ({ headers: [], auth: null, vars: [], scripts: emptyScripts() });

export const emptyCounts = () => ({ vars: 0, assertions: 0, examples: 0 });

/** Anything Bruno may store as a value (typed `{type, data}`, number, object) → string. */
export function valueToString(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'data' in value && 'type' in value) return valueToString(value.data);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function row(key, value, enabled = true) {
  return { key: String(key ?? ''), value: valueToString(value), enabled: enabled !== false };
}

const str = (v) => (v == null ? '' : String(v));

/**
 * Bruno's internal auth model — `{ mode, bearer:{token}, basic:{…}, apikey:{key,value,placement},
 * awsv4:{…}, oauth2:{grantType, authorizationUrl, accessTokenUrl, callbackUrl, clientId,
 * clientSecret, scope, pkce} }` — is what the JSON export carries, and what the .bru and
 * YAML front-ends build before calling this, so there is one place that reads it.
 */
export function authFromBrunoModel(auth) {
  if (!auth || typeof auth !== 'object') return { mode: 'none' };
  const mode = String(auth.mode || 'none');
  switch (mode) {
    case 'none':
    case 'inherit':
      return { mode };
    case 'bearer':
      return { mode, token: str(auth.bearer?.token) };
    case 'basic':
      return { mode, username: str(auth.basic?.username), password: str(auth.basic?.password) };
    case 'apikey': {
      const placement = str(auth.apikey?.placement || auth.apikey?.in).toLowerCase();
      return {
        mode,
        key: str(auth.apikey?.key),
        value: str(auth.apikey?.value),
        in: placement === 'queryparams' || placement === 'query' ? 'query' : 'header',
      };
    }
    case 'awsv4':
      return {
        mode,
        accessKeyId: str(auth.awsv4?.accessKeyId),
        secretAccessKey: str(auth.awsv4?.secretAccessKey),
        sessionToken: str(auth.awsv4?.sessionToken),
        service: str(auth.awsv4?.service),
        region: str(auth.awsv4?.region),
      };
    case 'oauth2': {
      const o = auth.oauth2 || {};
      return {
        mode,
        oauth2: {
          grant: str(o.grantType || 'authorization_code'),
          authUrl: str(o.authorizationUrl),
          tokenUrl: str(o.accessTokenUrl),
          callbackUrl: str(o.callbackUrl),
          clientId: str(o.clientId),
          clientSecret: str(o.clientSecret),
          scope: str(o.scope),
          pkce: o.pkce === true || o.pkce === 'true',
          accessToken: '',
        },
      };
    }
    default:
      return { mode: 'unsupported', unsupportedName: mode };
  }
}

/** Every string that can hold a script, for the unsupported-API scan. */
export function scriptsOf(scripts) {
  return [scripts?.pre, scripts?.post, scripts?.tests].filter((s) => typeof s === 'string' && s.trim());
}
